import { makeStyles } from "@mui/styles";
import { useEffect, useRef } from "react";
import { IOSView } from "react-device-detect";

import { getMediaLink } from "../../../utils/mediaLink";

const useStyles = makeStyles({
  container: {
    borderRadius: "30px",
    overflow: "hidden",
    width: "100%",
    display: "flex",
  },
  audio: {
    width: "100%",
    filter: "invert(1)",
    // TODO
    // this is a workaround to style the audio element which only works on wekbit browsers
    // in the future we'll need to implement a better way to style audio component for other browsers.
    // this is 'ok' for now.
    "&::-webkit-media-controls-panel": {
      background: "#cccccc",
    },
  },
});

// interface VoiceMemoPlaybackProps {
//   style: proptypes.object,
//   url: proptypes.string,
// };

export default function VoiceMemoPlayback(props) {
  const { style, url } = props;
  const audioRef = useRef(null);
  const classes = useStyles();
  // Handle iOS specific time updates
  useEffect(() => {
    if (!audioRef.current || !IOSView) return;

    const audio = audioRef.current;
    let progressInterval;

    const handleTimeUpdate = () => {
      // Force UI update by triggering a reflow
      audio.style.webkitTransform = "scale(1)";
    };

    const startProgressTracking = () => {
      // Clear any existing interval
      if (progressInterval) clearInterval(progressInterval);

      // Create new interval for progress updates
      progressInterval = setInterval(() => {
        if (!audio.paused) {
          handleTimeUpdate();
        }
      }, 100);
    };

    const stopProgressTracking = () => {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
    };

    // Add event listeners
    audio.addEventListener("play", startProgressTracking);
    audio.addEventListener("pause", stopProgressTracking);
    audio.addEventListener("ended", stopProgressTracking);
    audio.addEventListener("timeupdate", handleTimeUpdate);

    // Initial load for iOS
    audio.load();

    // Force an initial time update
    handleTimeUpdate();

    return () => {
      stopProgressTracking();
      if (audio) {
        audio.removeEventListener("play", startProgressTracking);
        audio.removeEventListener("pause", stopProgressTracking);
        audio.removeEventListener("ended", stopProgressTracking);
        audio.removeEventListener("timeupdate", handleTimeUpdate);
      }
    };
  }, [IOSView, url]);

  useEffect(() => {
    //https://bugs.chromium.org/p/chromium/issues/detail?id=642012
    const handleDurationUpdate = (event) => {
      event.target.currentTime = 0;
      event.target.removeEventListener("timeupdate", handleDurationUpdate);
    };

    if (
      audioRef.current &&
      //This handles chrome bug where the duration is Infinity or NaN
      (audioRef.current.duration === Infinity || isNaN(Number(audioRef.current.duration)))
    ) {
      audioRef.current.currentTime = 1e101; //set currentTime to a large number then the actuall duration
      audioRef.current.addEventListener("timeupdate", handleDurationUpdate);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener("timeupdate", handleDurationUpdate);
      }
    };
  }, [url]);

  // setup focus on audio element when it's rendered to allow keyboard shortcuts
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (!audioRef.current) return;

    const audio = audioRef.current;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        audio.pause();
      }
    };

    const handlePhoneCall = () => {
      audio.pause();
    };

    const handlePlaybackInterference = (event) => {
      if (event.target !== audio) {
        audio.pause();
      }
    };

    const handleAudioFocusLoss = () => {
      audio.pause();
    };

    const handleError = (error) => {
      console.error("Audio playback error:", error);
      audio.pause();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handleVisibilityChange);
    window.addEventListener("beforeunload", handleVisibilityChange);
    audio.addEventListener("pause", handlePlaybackInterference);
    audio.addEventListener("play", handlePlaybackInterference);
    audio.addEventListener("error", handleError);

    if ("ondeviceready" in document) {
      document.addEventListener("pause", handlePhoneCall);
      document.addEventListener("resign", handleAudioFocusLoss);
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleVisibilityChange);
      audio.removeEventListener("pause", handlePlaybackInterference);
      audio.removeEventListener("play", handlePlaybackInterference);
      audio.removeEventListener("error", handleError);

      if ("ondeviceready" in document) {
        document.removeEventListener("pause", handlePhoneCall);
        document.removeEventListener("resign", handleAudioFocusLoss);
      }
    };
  }, []);

  if (!url) {
    // no reason to render
    return null;
  }

  return (
    <div className={classes.container} style={style}>
      <audio
        ref={audioRef}
        tabIndex={0}
        data-testid="playback-audio"
        className={classes.audio}
        src={getMediaLink({ link: url })}
        controls
        controlsList="nodownload noplaybackrate"
        preload="auto"
      />
    </div>
  );
}
